import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ArrowLeft, Plus, Trash2, Upload, Save, Eye, EyeOff, ClipboardPaste, Table2, Info, Wand2, Dices,
} from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAX_COLS = 20;
const MAX_ROWS = 500;

/** Parse Excel/Sheets/Word clipboard TSV, or CSV. Returns { columns, rows }. */
const parseTabular = (raw) => {
  if (!raw) return null;
  // Strip trailing newlines/empty rows
  const lines = raw.replace(/\r\n?/g, "\n").split("\n").filter((l) => l.trim().length > 0);
  if (lines.length === 0) return null;

  // Detect delimiter — tabs (from Excel/Sheets) vs commas (CSV). Prefer tabs when present.
  const hasTabs = lines.some((l) => l.includes("\t"));
  const parseRow = (line) => {
    if (hasTabs) return line.split("\t");
    // Simple CSV parse honouring double-quoted cells
    const out = [];
    let cur = "";
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (inQ) {
        if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (c === '"') { inQ = false; }
        else { cur += c; }
      } else {
        if (c === '"') { inQ = true; }
        else if (c === ",") { out.push(cur); cur = ""; }
        else { cur += c; }
      }
    }
    out.push(cur);
    return out;
  };
  const rows = lines.map(parseRow);
  return rows;
};

/** Parse an HTML table from clipboard — the most reliable way to get Excel/Sheets structure. */
const parseHtmlTable = (html) => {
  if (!html || !html.includes("<table")) return null;
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const tbl = doc.querySelector("table");
    if (!tbl) return null;
    const rows = [];
    tbl.querySelectorAll("tr").forEach((tr) => {
      const cells = [];
      tr.querySelectorAll("td, th").forEach((td) => {
        // Excel encodes newlines in cells as <br>; preserve them as \n
        const html = td.innerHTML.replace(/<br\s*\/?>/gi, "\n");
        const tmp = document.createElement("div");
        tmp.innerHTML = html;
        cells.push((tmp.textContent || "").trim());
      });
      if (cells.length > 0) rows.push(cells);
    });
    return rows.length > 0 ? rows : null;
  } catch {
    return null;
  }
};

/** Read tabular data from a paste event or clipboard read result. */
const gridFromClipboard = (htmlOrNull, plainText) => {
  const fromHtml = parseHtmlTable(htmlOrNull);
  if (fromHtml && fromHtml.length > 0 && fromHtml.some((r) => r.length > 1)) return fromHtml;
  return parseTabular(plainText);
};

const TableDetailPage = () => {
  const { campaignId, tableId } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const fileInputRef = useRef(null);
  const gridRef = useRef(null);

  const [table, setTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [rolledRow, setRolledRow] = useState(null);

  // Text Import Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizText, setWizText] = useState("");
  const [wizCols, setWizCols] = useState(4);
  const [wizLinesPerRow, setWizLinesPerRow] = useState(1);
  const [wizMode, setWizMode] = useState("lines_per_row"); // "lines_per_row" | "blank_line" | "delimiter"
  const [wizDelim, setWizDelim] = useState("|");

  const fetchTable = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tables/${tableId}`, { withCredentials: true });
      setTable(res.data);
      setDirty(false);
    } catch { toast.error("Failed to load table"); navigate(`/campaign/${campaignId}/tables`); }
    finally { setLoading(false); }
  }, [tableId, campaignId, navigate]);

  useEffect(() => { fetchTable(); }, [fetchTable]);

  const patchTable = (fields) => {
    setTable((prev) => (prev ? { ...prev, ...fields } : prev));
    setDirty(true);
  };

  const save = async () => {
    if (!table) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/tables/${table.id}`, {
        name: table.name,
        description: table.description,
        columns: table.columns,
        rows: table.rows,
        visible_to_players: table.visible_to_players,
      }, { withCredentials: true });
      setTable(res.data);
      setDirty(false);
      toast.success("Table saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const addRow = () => {
    if (!table) return;
    if (table.rows.length >= MAX_ROWS) { toast.error(`Max ${MAX_ROWS} rows`); return; }
    patchTable({ rows: [...table.rows, Array(table.columns.length).fill("")] });
  };
  const removeRow = (idx) => {
    patchTable({ rows: table.rows.filter((_, i) => i !== idx) });
  };
  const addCol = () => {
    if (!table) return;
    if (table.columns.length >= MAX_COLS) { toast.error(`Max ${MAX_COLS} columns`); return; }
    patchTable({
      columns: [...table.columns, `Column ${table.columns.length + 1}`],
      rows: table.rows.map((r) => [...r, ""]),
    });
  };
  const removeCol = async (idx) => {
    if (table.columns.length <= 1) { toast.error("A table needs at least one column"); return; }
    if (!await confirm("Remove column?", `"${table.columns[idx]}" and its data will be removed from every row.`)) return;
    patchTable({
      columns: table.columns.filter((_, i) => i !== idx),
      rows: table.rows.map((r) => r.filter((_, i) => i !== idx)),
    });
  };
  const setCol = (idx, value) => {
    const cols = [...table.columns];
    cols[idx] = value;
    patchTable({ columns: cols });
  };
  const setCell = (rIdx, cIdx, value) => {
    const rows = table.rows.map((row) => [...row]);
    rows[rIdx][cIdx] = value;
    patchTable({ rows });
  };

  /** Roll on this table — pick a random row, highlight it and toast the result. */
  const rollOnTable = () => {
    if (!table || table.rows.length === 0) { toast.error("Add some rows before rolling."); return; }
    const idx = Math.floor(Math.random() * table.rows.length);
    setRolledRow(idx);
    const row = table.rows[idx] || [];
    const summary = row.map((c) => String(c || "").trim()).filter(Boolean).join(" — ") || "(empty row)";
    toast.success(`🎲 Rolled row ${idx + 1}: ${summary}`, { duration: 6000 });
    // Scroll the rolled row into view.
    setTimeout(() => {
      gridRef.current?.querySelector(`[data-testid="table-row-${idx}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  /** Paste handler on any cell — if clipboard contains tabular data, replace rows starting at that cell. */
  const handleCellPaste = (e, rIdx, cIdx) => {
    const text = e.clipboardData.getData("text/plain") || "";
    const html = e.clipboardData.getData("text/html") || "";
    // If clipboard has an HTML <table>, always treat as multi-cell paste
    // Otherwise only intercept when plain text contains tabs or newlines
    const hasTable = html.includes("<table");
    if (!hasTable && !text.includes("\t") && !text.includes("\n")) return; // single-cell paste
    const grid = gridFromClipboard(html, text);
    if (!grid || grid.length === 0) return;
    e.preventDefault();
    // Fit paste into existing shape — grow rows/cols as needed
    const neededCols = Math.max(table.columns.length, cIdx + Math.max(...grid.map((r) => r.length)));
    const nextCols = [...table.columns];
    while (nextCols.length < neededCols && nextCols.length < MAX_COLS) {
      nextCols.push(`Column ${nextCols.length + 1}`);
    }
    // Ensure all existing rows have the new width
    const nextRows = table.rows.map((r) => {
      const copy = [...r];
      while (copy.length < nextCols.length) copy.push("");
      return copy;
    });
    // Ensure enough rows exist
    while (nextRows.length < rIdx + grid.length && nextRows.length < MAX_ROWS) {
      nextRows.push(Array(nextCols.length).fill(""));
    }
    // Write pasted cells
    grid.forEach((row, ri) => {
      const targetRow = nextRows[rIdx + ri];
      if (!targetRow) return;
      row.forEach((val, ci) => {
        const col = cIdx + ci;
        if (col < targetRow.length) targetRow[col] = String(val || "");
      });
    });
    patchTable({ columns: nextCols, rows: nextRows });
    toast.success(`Pasted ${grid.length} row${grid.length === 1 ? "" : "s"}`);
  };

  /** Bulk paste from Textarea — treats clipboard as full table replacement (first row = headers). */
  const bulkPasteReplace = async () => {
    try {
      let html = "";
      let text = "";
      // Try the richer clipboard API first (HTML preserves Excel/Sheets grid structure)
      if (navigator.clipboard?.read) {
        try {
          const items = await navigator.clipboard.read();
          for (const item of items) {
            if (item.types.includes("text/html")) {
              html = await (await item.getType("text/html")).text();
            }
            if (item.types.includes("text/plain")) {
              text = await (await item.getType("text/plain")).text();
            }
          }
        } catch { /* fall through to readText */ }
      }
      if (!text && !html) {
        text = await navigator.clipboard.readText();
      }
      const grid = gridFromClipboard(html, text);
      if (!grid || grid.length === 0) { toast.error("Clipboard is empty or not tabular"); return; }
      if (!await confirm("Replace table with clipboard?", "The current columns and rows will be replaced. First row of the paste is treated as headers.")) return;
      const headers = grid[0].map((h) => String(h || "").trim() || "Column").slice(0, MAX_COLS);
      const rows = grid.slice(1, 1 + MAX_ROWS).map((r) => {
        const cells = r.slice(0, headers.length).map((v) => String(v || ""));
        while (cells.length < headers.length) cells.push("");
        return cells;
      });
      patchTable({ columns: headers, rows });
      toast.success(`Loaded ${rows.length} rows from clipboard`);
    } catch {
      toast.error("Could not read clipboard. Try pasting directly into a cell instead (Ctrl/⌘+V).");
    }
  };

  /** Text-Import wizard parser — takes messy PDF/plain-text and coerces into a grid. */
  const parseWizardText = () => {
    const raw = (wizText || "").replace(/\r\n?/g, "\n");
    const nCols = Math.max(1, Math.min(MAX_COLS, Number(wizCols) || 1));
    if (!raw.trim()) return { rows: [], nCols };

    let groups = []; // each group is an array of raw lines forming one row

    if (wizMode === "delimiter") {
      // Each line = 1 row; cells split by user-chosen delimiter
      const delim = wizDelim || "|";
      groups = raw.split("\n").filter((l) => l.trim().length > 0).map((line) => line.split(delim));
    } else if (wizMode === "blank_line") {
      // Rows separated by blank lines; lines within a group each become a cell
      const chunks = raw.split(/\n\s*\n/).map((c) => c.split("\n").map((s) => s.trim()).filter(Boolean));
      groups = chunks.filter((c) => c.length > 0);
    } else {
      // "lines_per_row" — chunk consecutive non-empty lines into groups of N
      const lines = raw.split("\n").filter((l) => l.trim().length > 0);
      const per = Math.max(1, Number(wizLinesPerRow) || 1);
      for (let i = 0; i < lines.length; i += per) {
        groups.push(lines.slice(i, i + per));
      }
    }

    // Convert each group into a row of exactly nCols cells
    const rowsOut = groups.map((group) => {
      if (group.length === nCols) return group.map((c) => String(c || "").trim());
      if (group.length < nCols) {
        // pad missing cells with empty strings
        return [...group.map((c) => String(c || "").trim()), ...Array(nCols - group.length).fill("")];
      }
      // group.length > nCols — distribute lines across N cells (evenly, with leftover joined into last)
      const perCell = Math.floor(group.length / nCols);
      const remainder = group.length - perCell * nCols;
      const cells = [];
      let idx = 0;
      for (let c = 0; c < nCols; c++) {
        // Last cell absorbs the remainder
        const take = perCell + (c === nCols - 1 ? remainder : 0);
        cells.push(group.slice(idx, idx + take).map((s) => String(s || "").trim()).filter(Boolean).join(" "));
        idx += take;
      }
      return cells;
    });

    return { rows: rowsOut, nCols };
  };

  const applyWizard = async () => {
    const { rows: parsed, nCols } = parseWizardText();
    if (parsed.length === 0) { toast.error("Nothing to import — paste some text first."); return; }
    if (!await confirm("Replace table with imported text?", `${parsed.length} rows × ${nCols} columns will replace the current table. First row will be used as headers.`)) return;
    const headers = parsed[0].map((h, i) => (h && h.trim()) || `Column ${i + 1}`);
    const rows = parsed.slice(1);
    patchTable({ columns: headers, rows });
    setWizardOpen(false);
    toast.success(`Imported ${rows.length} rows`);
  };

  const openWizard = () => {
    setWizText("");
    setWizCols(Math.max(2, (table?.columns?.length || 4)));
    setWizLinesPerRow(1);
    setWizMode("lines_per_row");
    setWizDelim("|");
    setWizardOpen(true);
  };

  const onFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const grid = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, blankrows: false });
      if (!grid || grid.length === 0) { toast.error("Spreadsheet is empty"); return; }
      if (!await confirm("Replace table with spreadsheet?", `${file.name} will replace the current columns and rows. First row of the sheet is treated as headers.`)) return;
      const headers = (grid[0] || []).map((h) => String(h || "").trim() || "Column").slice(0, MAX_COLS);
      const rows = grid.slice(1, 1 + MAX_ROWS).map((r) => {
        const arr = Array.isArray(r) ? r : [];
        const cells = arr.slice(0, headers.length).map((v) => String(v ?? ""));
        while (cells.length < headers.length) cells.push("");
        return cells;
      });
      patchTable({ columns: headers, rows });
      toast.success(`Loaded ${rows.length} rows from ${file.name}`);
    } catch (err) {
      toast.error("Failed to parse spreadsheet");
    }
  };

  if (loading || !table) {
    return (
      <Layout>
        <div className="p-8 text-muted-foreground text-sm">Loading table&hellip;</div>
      </Layout>
    );
  }

  return (
    <Layout tabLabel={table.name}>
      <div className="w-full" data-testid="table-detail-page">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/campaign/${campaignId}/tables`)}
              data-testid="table-back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Tables
            </Button>
            <span className="text-muted-foreground text-sm flex items-center gap-1">
              <Table2 className="w-4 h-4" /> {table.columns.length} cols &middot; {table.rows.length} rows
            </span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="table-visibility-toggle-label">
              {table.visible_to_players ? <Eye className="w-4 h-4 text-primary" /> : <EyeOff className="w-4 h-4" />}
              <span>Share with players</span>
              <Switch
                checked={!!table.visible_to_players}
                onCheckedChange={(v) => patchTable({ visible_to_players: v })}
                data-testid="table-visibility-switch"
              />
            </label>
            <Button
              onClick={save}
              disabled={saving || !dirty}
              className="bg-primary text-primary-foreground rounded-sm"
              data-testid="table-save-button"
            >
              <Save className="w-4 h-4 mr-1" />
              {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
            </Button>
          </div>
        </div>

        {/* Name + description */}
        <div className="mb-4 space-y-2">
          <Input
            value={table.name}
            onChange={(e) => patchTable({ name: e.target.value })}
            className="text-2xl font-serif h-auto py-2 border-0 border-b border-border rounded-none focus-visible:ring-0 focus-visible:border-primary bg-transparent"
            placeholder="Table name"
            data-testid="table-name-input"
          />
          <Textarea
            value={table.description || ""}
            onChange={(e) => patchTable({ description: e.target.value })}
            className="text-sm border-0 border-b border-border/40 rounded-none focus-visible:ring-0 focus-visible:border-primary bg-transparent resize-none"
            placeholder="Optional description or usage notes…"
            rows={2}
            data-testid="table-description-input"
          />
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Button size="sm" onClick={rollOnTable} className="bg-primary text-primary-foreground rounded-sm" data-testid="roll-on-table-button">
            <Dices className="w-3.5 h-3.5 mr-1" /> Roll on this table
          </Button>
          <div className="w-px h-6 bg-border mx-1" />
          <Button size="sm" variant="outline" onClick={addRow} data-testid="add-row-button">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add row
          </Button>
          <Button size="sm" variant="outline" onClick={addCol} data-testid="add-col-button">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add column
          </Button>
          <div className="w-px h-6 bg-border mx-1" />
          <Button size="sm" variant="outline" onClick={bulkPasteReplace} data-testid="bulk-paste-button">
            <ClipboardPaste className="w-3.5 h-3.5 mr-1" /> Paste from clipboard
          </Button>
          <Button size="sm" variant="outline" onClick={openWizard} data-testid="wizard-button">
            <Wand2 className="w-3.5 h-3.5 mr-1" /> Paste from PDF / Word
          </Button>
          <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="upload-file-button">
            <Upload className="w-3.5 h-3.5 mr-1" /> Upload .xlsx / .csv
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv,.tsv,text/csv"
            className="hidden"
            onChange={onFileUpload}
            data-testid="table-file-input"
          />
          <span className="text-[11px] text-muted-foreground flex items-center gap-1 ml-1">
            <Info className="w-3 h-3" />
            Tip: paste a range from Excel/Sheets directly into any cell to fill many at once.
          </span>
        </div>

        {/* PARCHMENT-STYLED GRID */}
        <div
          ref={gridRef}
          className="rounded-md overflow-hidden border-2 border-[#8a6a2e]/60 shadow-[0_10px_30px_-15px_rgba(0,0,0,0.6)]"
          data-testid="table-grid-wrapper"
        >
          <div
            className="overflow-auto max-h-[calc(100vh-360px)] min-h-[400px]"
            style={{
              backgroundColor: "#f4e9c9",
              backgroundImage:
                "radial-gradient(circle at 20% 30%, rgba(168,128,60,0.10) 0, transparent 60%)," +
                "radial-gradient(circle at 80% 70%, rgba(120,80,30,0.10) 0, transparent 55%)",
            }}
          >
            <table className="w-full border-collapse font-serif text-[#3a2a12]" data-testid="parchment-table">
              <thead>
                <tr className="bg-[#8a6a2e]/15">
                  {table.columns.map((col, cIdx) => (
                    <th
                      key={cIdx}
                      className="border border-[#8a6a2e]/40 p-0 align-top min-w-[140px]"
                    >
                      <div className="flex items-center group">
                        <input
                          value={col}
                          onChange={(e) => setCol(cIdx, e.target.value)}
                          className="flex-1 bg-transparent px-3 py-2 text-sm font-bold uppercase tracking-wide outline-none focus:bg-[#f9f0d6]"
                          placeholder="Header"
                          data-testid={`table-header-${cIdx}`}
                        />
                        <button
                          type="button"
                          onClick={() => removeCol(cIdx)}
                          className="opacity-0 group-hover:opacity-100 text-[#8a1a1a]/60 hover:text-[#8a1a1a] px-2"
                          title="Delete column"
                          data-testid={`delete-col-${cIdx}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </th>
                  ))}
                  <th className="w-10 border border-[#8a6a2e]/40 bg-[#8a6a2e]/10" />
                </tr>
              </thead>
              <tbody>
                {table.rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={table.columns.length + 1}
                      className="text-center text-sm text-[#3a2a12]/60 py-10 italic border border-[#8a6a2e]/30"
                    >
                      No rows yet. Click <span className="font-bold">Add row</span> or paste from Excel/Sheets.
                    </td>
                  </tr>
                ) : (
                  table.rows.map((row, rIdx) => (
                    <tr
                      key={rIdx}
                      className={`${rIdx % 2 === 0 ? "bg-[#dfe9c7]/60" : "bg-transparent"} ${rolledRow === rIdx ? "outline outline-2 outline-[#8a1a1a] bg-[#f6d98a]/70" : ""}`}
                      data-testid={`table-row-${rIdx}`}
                    >
                      {row.map((cell, cIdx) => (
                        <td key={cIdx} className="border border-[#8a6a2e]/25 p-0 align-top">
                          <textarea
                            value={cell}
                            onChange={(e) => setCell(rIdx, cIdx, e.target.value)}
                            onPaste={(e) => handleCellPaste(e, rIdx, cIdx)}
                            rows={1}
                            className="w-full bg-transparent px-3 py-2 text-sm outline-none focus:bg-[#f9f0d6] resize-none leading-snug"
                            data-testid={`table-cell-${rIdx}-${cIdx}`}
                          />
                        </td>
                      ))}
                      <td className="border border-[#8a6a2e]/25 text-center align-middle group">
                        <button
                          type="button"
                          onClick={() => removeRow(rIdx)}
                          className="opacity-30 group-hover:opacity-100 text-[#8a1a1a]/70 hover:text-[#8a1a1a] p-1"
                          title="Delete row"
                          data-testid={`delete-row-${rIdx}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {dirty && (
          <p className="text-xs text-primary mt-3 flex items-center gap-1" data-testid="unsaved-indicator">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            Unsaved changes &mdash; click <span className="font-semibold">Save changes</span> to persist.
          </p>
        )}

        {/* ============ TEXT IMPORT WIZARD ============ */}
        <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="wizard-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-primary" /> Paste from PDF / Word / plain text
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-primary/10 border border-primary/20 rounded-md p-3 text-xs text-muted-foreground space-y-1">
                <p className="text-foreground font-semibold flex items-center gap-1">
                  <Info className="w-3.5 h-3.5" /> Why is this needed?
                </p>
                <p>
                  When you copy from a PDF or Word doc, the grid structure is lost — the clipboard
                  just has plain text with newlines. Use this tool to rebuild the grid.
                </p>
                <p className="text-primary">
                  Tip: For best results, paste PDF text into <strong>Excel or Google Sheets first</strong> &mdash; 
                  they have &ldquo;Text to columns&rdquo; wizards &mdash; then copy from there into the table
                  (that path preserves the grid perfectly).
                </p>
              </div>

              {/* Step 1 — paste */}
              <div>
                <label className="text-xs font-semibold text-foreground mb-1 block">1. Paste your text here</label>
                <Textarea
                  value={wizText}
                  onChange={(e) => setWizText(e.target.value)}
                  className="font-mono text-xs min-h-[140px] max-h-[240px]"
                  placeholder="Paste text from your PDF or Word document here…"
                  data-testid="wizard-text-input"
                />
              </div>

              {/* Step 2 — settings */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-foreground mb-1 block">2. Columns per row</label>
                  <Input
                    type="number"
                    min={1}
                    max={MAX_COLS}
                    value={wizCols}
                    onChange={(e) => setWizCols(Number(e.target.value) || 1)}
                    data-testid="wizard-cols-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-foreground mb-1 block">3. How are rows separated?</label>
                  <select
                    value={wizMode}
                    onChange={(e) => setWizMode(e.target.value)}
                    className="w-full h-10 px-3 py-2 text-sm bg-background border border-input rounded-md"
                    data-testid="wizard-mode-select"
                  >
                    <option value="lines_per_row">Fixed number of lines per row</option>
                    <option value="blank_line">Blank line between rows</option>
                    <option value="delimiter">Each line is a row, split by a character</option>
                  </select>
                </div>
              </div>

              {wizMode === "lines_per_row" && (
                <div>
                  <label className="text-xs font-semibold text-foreground mb-1 block">
                    Lines per row
                    <span className="text-muted-foreground font-normal ml-2">
                      (count how many lines each entry takes in your PDF — e.g. 5 for the Astral Sea example)
                    </span>
                  </label>
                  <Input
                    type="number"
                    min={1}
                    max={50}
                    value={wizLinesPerRow}
                    onChange={(e) => setWizLinesPerRow(Number(e.target.value) || 1)}
                    data-testid="wizard-lines-input"
                  />
                </div>
              )}

              {wizMode === "delimiter" && (
                <div>
                  <label className="text-xs font-semibold text-foreground mb-1 block">
                    Delimiter character (put between each cell in your text)
                  </label>
                  <Input
                    value={wizDelim}
                    onChange={(e) => setWizDelim(e.target.value || "|")}
                    className="w-20 font-mono"
                    data-testid="wizard-delim-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Edit your pasted text above to put <code className="text-primary">{wizDelim}</code> between each cell,
                    then use one line per row.
                  </p>
                </div>
              )}

              {/* Live preview */}
              <div>
                <label className="text-xs font-semibold text-foreground mb-1 block">4. Preview</label>
                <div className="border border-border rounded-md overflow-hidden max-h-[280px] overflow-y-auto bg-background">
                  {(() => {
                    const { rows: parsed, nCols } = parseWizardText();
                    if (parsed.length === 0) {
                      return <div className="p-4 text-xs text-muted-foreground italic">Preview will appear here…</div>;
                    }
                    return (
                      <table className="w-full text-xs" data-testid="wizard-preview-table">
                        <thead className="bg-primary/10">
                          <tr>
                            {parsed[0].map((h, i) => (
                              <th key={i} className="border border-border px-2 py-1 text-left font-bold uppercase tracking-wide text-foreground">{h || `Col ${i + 1}`}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {parsed.slice(1).map((r, ri) => (
                            <tr key={ri} className={ri % 2 === 0 ? "bg-muted/30" : ""}>
                              {r.map((c, ci) => (
                                <td key={ci} className="border border-border px-2 py-1 align-top">{c}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  First row becomes the table headers. {(() => { const p = parseWizardText(); return p.rows.length > 0 ? `${p.rows.length - 1} data rows will be imported.` : ""; })()}
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setWizardOpen(false)} data-testid="wizard-cancel">Cancel</Button>
                <Button onClick={applyWizard} className="bg-primary text-primary-foreground" data-testid="wizard-import-button">
                  <Wand2 className="w-3.5 h-3.5 mr-1" /> Import into table
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TableDetailPage;
